import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import {
  doc, getDoc, setDoc, updateDoc, increment, serverTimestamp
} from 'firebase/firestore';

const StatsContext = createContext(null);
export const useStats = () => useContext(StatsContext);

// Intervalos da repetição espaçada: quantos dias até o card voltar a ficar
// disponível, de acordo com o resultado da última resposta.
const DIAS_POR_RESULTADO = { erro: 0, dificil: 1, acerto: 3 };

function hojeISO() {
  return new Date().toISOString().split('T')[0];
}

function somarDiasISO(dias) {
  const data = new Date();
  data.setDate(data.getDate() + dias);
  return data.toISOString().split('T')[0];
}

export const StatsProvider = ({ children }) => {
  const { usuario } = useAuth();
  const [stats, setStats] = useState({});
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    if (usuario) {
      carregarStats();
    } else {
      setStats({});
      setCarregando(false);
    }
  }, [usuario]);

  const carregarStats = async () => {
    try {
      setCarregando(true);
      const ref = doc(db, 'stats', usuario.uid);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setStats(snap.data());
      } else {
        const statsIniciais = {
          acertos: {}, erros: {}, dificeis: {}, vistos: {}, ultimaRevisao: {}, progresso: {},
          ultimoResultado: {}, proximaRevisao: {}
        };
        await setDoc(ref, statsIniciais);
        setStats(statsIniciais);
      }
    } catch (err) {
      console.error('Erro ao carregar stats:', err);
    } finally {
      setCarregando(false);
    }
  };

  const registrarAcerto = async (cardId, materiaId) => {
    if (!usuario) return;
    const ref = doc(db, 'stats', usuario.uid);
    const hoje = hojeISO();

    const novasStats = { ...stats };
    novasStats.acertos = { ...novasStats.acertos, [cardId]: (novasStats.acertos?.[cardId] || 0) + 1 };
    novasStats.vistos = { ...novasStats.vistos, [cardId]: (novasStats.vistos?.[cardId] || 0) + 1 };
    novasStats.ultimaRevisao = { ...novasStats.ultimaRevisao, [cardId]: hoje };
    novasStats.ultimoResultado = { ...novasStats.ultimoResultado, [cardId]: 'acerto' };
    novasStats.proximaRevisao = { ...novasStats.proximaRevisao, [cardId]: somarDiasISO(DIAS_POR_RESULTADO.acerto) };

    // Remove 1 erro e 1 dificuldade ao acertar (aprendizagem progressiva)
    if (novasStats.erros?.[cardId] > 0) {
      novasStats.erros = { ...novasStats.erros, [cardId]: Math.max(0, novasStats.erros[cardId] - 1) };
    }
    if (novasStats.dificeis?.[cardId] > 0) {
      novasStats.dificeis = { ...novasStats.dificeis, [cardId]: Math.max(0, novasStats.dificeis[cardId] - 1) };
    }

    setStats(novasStats);

    try {
      await setDoc(ref, novasStats, { merge: true });
    } catch (err) {
      console.error('Erro ao salvar acerto:', err);
    }
  };

  const registrarErro = async (cardId, materiaId) => {
    if (!usuario) return;
    const hoje = hojeISO();
    const ref = doc(db, 'stats', usuario.uid);

    const novasStats = { ...stats };
    novasStats.erros = { ...novasStats.erros, [cardId]: (novasStats.erros?.[cardId] || 0) + 1 };
    novasStats.vistos = { ...novasStats.vistos, [cardId]: (novasStats.vistos?.[cardId] || 0) + 1 };
    novasStats.ultimaRevisao = { ...novasStats.ultimaRevisao, [cardId]: hoje };
    novasStats.ultimoResultado = { ...novasStats.ultimoResultado, [cardId]: 'erro' };
    novasStats.proximaRevisao = { ...novasStats.proximaRevisao, [cardId]: somarDiasISO(DIAS_POR_RESULTADO.erro) };

    setStats(novasStats);

    try {
      await setDoc(ref, novasStats, { merge: true });
    } catch (err) {
      console.error('Erro ao salvar erro:', err);
    }
  };

  // "Com dificuldade" — categoria intermediária entre acerto e erro, contabilizada
  // separadamente para a repetição espaçada (aparece com frequência moderada)
  const registrarDificil = async (cardId, materiaId) => {
    if (!usuario) return;
    const hoje = hojeISO();
    const ref = doc(db, 'stats', usuario.uid);

    const novasStats = { ...stats };
    novasStats.dificeis = { ...novasStats.dificeis, [cardId]: (novasStats.dificeis?.[cardId] || 0) + 1 };
    novasStats.vistos = { ...novasStats.vistos, [cardId]: (novasStats.vistos?.[cardId] || 0) + 1 };
    novasStats.ultimaRevisao = { ...novasStats.ultimaRevisao, [cardId]: hoje };
    novasStats.ultimoResultado = { ...novasStats.ultimoResultado, [cardId]: 'dificil' };
    novasStats.proximaRevisao = { ...novasStats.proximaRevisao, [cardId]: somarDiasISO(DIAS_POR_RESULTADO.dificil) };

    setStats(novasStats);

    try {
      await setDoc(ref, novasStats, { merge: true });
    } catch (err) {
      console.error('Erro ao salvar dificuldade:', err);
    }
  };

  const getTotalAcertos = () =>
    Object.values(stats.acertos || {}).reduce((a, b) => a + b, 0);

  const getTotalErros = () =>
    Object.values(stats.erros || {}).reduce((a, b) => a + b, 0);

  // Tier de prioridade da repetição espaçada, baseado no último resultado
  // (não na contagem histórica): não lembrei > com dificuldade > lembrei > novo.
  const TIER_PRIORIDADE = { erro: 3, dificil: 2, acerto: 1 };

  const getPesoPrioridade = useCallback((cardId) => {
    const resultado = stats.ultimoResultado?.[cardId];
    return resultado ? (TIER_PRIORIDADE[resultado] || 0) : 0;
  }, [stats]);

  // Ordena os cards por prioridade, com uma pequena variação aleatória dentro de
  // cada tier para não repetir sempre a mesma ordem entre sessões com o mesmo histórico.
  const ordenarPorPrioridade = useCallback((flashcards) => {
    return [...flashcards]
      .map(card => ({ card, peso: getPesoPrioridade(card.id) + Math.random() * 0.9 }))
      .sort((a, b) => b.peso - a.peso)
      .map(item => item.card);
  }, [getPesoPrioridade]);

  // Filtra os cards que já podem ser estudados hoje: nunca vistos (sempre
  // disponíveis) ou cuja proximaRevisao (agendada por registrarAcerto/Erro/Dificil)
  // já chegou. É isso que faz o "aparece só depois de N dias" valer de verdade,
  // em vez de só reordenar a fila.
  const getCardsDisponiveis = useCallback((flashcards) => {
    const hoje = hojeISO();
    return flashcards.filter(card => {
      const proxima = stats.proximaRevisao?.[card.id];
      return !proxima || proxima <= hoje;
    });
  }, [stats]);

  const getCardsDificeis = (flashcards) =>
    ordenarPorPrioridade(
      flashcards.filter(c => (stats.erros?.[c.id] || 0) > 0 || (stats.dificeis?.[c.id] || 0) > 0)
    );

  const getTaxaAcerto = () => {
    const a = getTotalAcertos();
    const e = getTotalErros();
    return a + e > 0 ? Math.round((a / (a + e)) * 100) : 0;
  };

  const getStatsMateria = (materiaId, flashcards) => {
    const cards = flashcards.filter(c => c.materia === materiaId);
    const acertos = cards.reduce((s, c) => s + (stats.acertos?.[c.id] || 0), 0);
    const erros = cards.reduce((s, c) => s + (stats.erros?.[c.id] || 0), 0);
    const vistos = cards.filter(c => (stats.vistos?.[c.id] || 0) > 0).length;
    const taxa = acertos + erros > 0 ? Math.round((acertos / (acertos + erros)) * 100) : null;
    return { acertos, erros, vistos, taxa, total: cards.length };
  };

  // Salva o último card estudado em uma matéria, para retomar de onde parou
  const salvarProgresso = async (materiaId, cardId) => {
    if (!usuario || !materiaId) return;
    const ref = doc(db, 'stats', usuario.uid);
    const novoProgresso = { ...stats.progresso, [materiaId]: cardId };

    setStats(prev => ({ ...prev, progresso: novoProgresso }));

    try {
      await setDoc(ref, { progresso: novoProgresso }, { merge: true });
    } catch (err) {
      console.error('Erro ao salvar progresso:', err);
    }
  };

  // Limpa o progresso salvo de uma matéria (sessão concluída ou reiniciada)
  const limparProgresso = async (materiaId) => {
    if (!usuario || !materiaId) return;
    const ref = doc(db, 'stats', usuario.uid);
    const novoProgresso = { ...stats.progresso };
    delete novoProgresso[materiaId];

    setStats(prev => ({ ...prev, progresso: novoProgresso }));

    try {
      await setDoc(ref, { progresso: novoProgresso }, { merge: true });
    } catch (err) {
      console.error('Erro ao limpar progresso:', err);
    }
  };

  // Registra o resultado de um simulado de questões online (acumulativo).
  // Usado pelo Ranking/Dashboard/Home para separar pontuação de questões da de flashcards.
  const registrarResultadoSimulado = async (acertos, erros) => {
    if (!usuario) return;
    const ref = doc(db, 'stats', usuario.uid);
    // Se o valor salvo anteriormente não for um número (dado legado/corrompido),
    // trata como 0 em vez de propagar o valor inválido.
    const baseAcertos = typeof stats.acertosQuestoes === 'number' ? stats.acertosQuestoes : 0;
    const baseErros = typeof stats.errosQuestoes === 'number' ? stats.errosQuestoes : 0;
    const acertosQuestoes = baseAcertos + acertos;
    const errosQuestoes = baseErros + erros;

    setStats(prev => ({ ...prev, acertosQuestoes, errosQuestoes }));

    try {
      await setDoc(ref, { acertosQuestoes, errosQuestoes }, { merge: true });
    } catch (err) {
      console.error('Erro ao salvar resultado do simulado:', err);
    }
  };

  const adicionarCardPersonalizado = async (card) => {
    if (!usuario) return;
    const ref = doc(db, 'cards_personalizados', usuario.uid);
    const snap = await getDoc(ref);
    const cards = snap.exists() ? snap.data().cards || [] : [];
    const novosCards = [...cards, { ...card, id: `custom_${Date.now()}`, criadoEm: new Date().toISOString() }];
    await setDoc(ref, { cards: novosCards });
    return novosCards;
  };

  const getCardsPersonalizados = async () => {
    if (!usuario) return [];
    const ref = doc(db, 'cards_personalizados', usuario.uid);
    const snap = await getDoc(ref);
    return snap.exists() ? snap.data().cards || [] : [];
  };

  return (
    <StatsContext.Provider value={{
      stats,
      carregando,
      registrarAcerto,
      registrarErro,
      registrarDificil,
      getTotalAcertos,
      getTotalErros,
      getTaxaAcerto,
      getCardsDificeis,
      getPesoPrioridade,
      ordenarPorPrioridade,
      getCardsDisponiveis,
      getStatsMateria,
      salvarProgresso,
      limparProgresso,
      registrarResultadoSimulado,
      adicionarCardPersonalizado,
      getCardsPersonalizados,
      recarregar: carregarStats
    }}>
      {children}
    </StatsContext.Provider>
  );
};
